import { useState } from "react";
import { motion } from "framer-motion";
import { CreditCard, Clock, CheckCircle2, Zap, Calendar, Shield, ArrowRight } from "lucide-react";

export default function StepPaymentChoice({ formData, setFormData, onPayNow, onPayLater }) {
  const [selectedChoice, setSelectedChoice] = useState(formData.paymentChoice || null);

  const handleChoice = (choice) => {
    if (choice === "now") return;
    setSelectedChoice(choice);
    setFormData((prev) => ({ ...prev, paymentChoice: choice }));
  };

  const handleProceed = () => {
    if (selectedChoice === "later") onPayLater();
  };

  const features = {
    now: ["Instant confirmation and ticket delivery", "Secure payment processing", "Priority support and assistance", "Immediate access to event materials"],
    later: ["Reserve your spot without payment", "Payment link sent to your email", "Flexible payment deadline", "Pay at your convenience"],
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="mx-auto max-w-5xl">
      <div className="mb-8 text-center">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200 }} className="inline-flex justify-center items-center mb-4 w-16 h-16 text-white bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl shadow-xl">
          <CreditCard size={32} />
        </motion.div>
        <h2 className="mb-2 text-3xl font-bold text-gray-900">Choose Your Payment Option</h2>
        <p className="text-base text-gray-600">Select how you would like to complete your registration payment</p>
      </div>
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex gap-3 items-center p-4 mb-8 bg-gradient-to-r from-green-500 to-green-600 rounded-xl shadow-lg">
        <div className="flex justify-center items-center w-10 h-10 bg-white rounded-full shrink-0">
          <CheckCircle2 size={20} className="text-green-600" />
        </div>
        <div>
          <p className="text-sm font-bold text-white">Registration Submitted Successfully</p>
          <p className="text-xs text-green-100">Now complete your payment to secure the spot</p>
        </div>
      </motion.div>
      <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2">
        <motion.div className="relative p-8 text-left rounded-2xl border-2 bg-gray-50 border-gray-300 opacity-60 cursor-not-allowed">
          <div className="flex absolute top-4 right-4 gap-2 items-center px-3 py-1 text-xs font-bold text-gray-500 bg-gray-200 rounded-full">
            <Zap size={12} /> COMING SOON
          </div>
          <div className="inline-flex justify-center items-center mb-4 w-14 h-14 rounded-xl bg-gray-200">
            <CreditCard size={24} className="text-gray-400" />
          </div>
          <h3 className="mb-2 text-2xl font-bold text-gray-500">Pay Now</h3>
          <p className="mb-4 text-sm text-gray-500">Complete your payment immediately and get instant confirmation</p>
          <ul className="mb-6 space-y-2">
            {features.now.map((f, i) => (
              <li key={i} className="flex gap-2 items-start text-sm text-gray-500">
                <CheckCircle2 size={16} className="mt-0.5 text-gray-400 shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <div className="flex gap-2 items-center px-4 py-2 text-sm font-semibold text-gray-500 bg-gray-200 rounded-lg">
            <Clock size={16} /> Temporarily Unavailable
          </div>
        </motion.div>
        <motion.button
          type="button"
          onClick={() => handleChoice("later")}
          whileHover={{ scale: 1.02, y: -4 }}
          whileTap={{ scale: 0.98 }}
          className={"relative p-8 text-left rounded-2xl border-2 transition-all " + (selectedChoice === "later" ? "bg-gradient-to-br from-blue-50 via-blue-100 to-blue-50 border-blue-500 shadow-2xl" : "bg-white border-gray-200 hover:border-blue-300 hover:shadow-xl")}
        >
          <div className={"inline-flex justify-center items-center mb-4 w-14 h-14 rounded-xl " + (selectedChoice === "later" ? "bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg" : "bg-gray-100")}>
            <Clock size={24} className={selectedChoice === "later" ? "text-white" : "text-gray-400"} />
          </div>
          <h3 className="mb-2 text-2xl font-bold text-gray-900">Pay Later</h3>
          <p className="mb-4 text-sm text-gray-600">Reserve your spot now and complete payment at your convenience</p>
          <ul className="mb-6 space-y-2">
            {features.later.map((f, i) => (
              <motion.li key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }} className="flex gap-2 items-start text-sm text-gray-700">
                <CheckCircle2 size={16} className="mt-0.5 text-blue-500 shrink-0" />
                <span>{f}</span>
              </motion.li>
            ))}
          </ul>
          {selectedChoice === "later" && (
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="flex gap-2 items-center px-4 py-2 text-sm font-bold text-blue-700 bg-blue-200 rounded-lg">
              <CheckCircle2 size={16} /> Selected
            </motion.div>
          )}
        </motion.button>
      </div>
      <div className="grid grid-cols-1 gap-4 mb-8 md:grid-cols-2">
        <div className="flex gap-3 items-start p-4 bg-gradient-to-br from-purple-50 to-white rounded-xl border border-purple-200">
          <Shield size={20} className="mt-0.5 text-purple-600 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-purple-900">Secure Payment</p>
            <p className="text-xs text-purple-700">All payments are processed securely with industry-standard encryption</p>
          </div>
        </div>
        <div className="flex gap-3 items-start p-4 bg-gradient-to-br from-amber-50 to-white rounded-xl border border-amber-200">
          <Calendar size={20} className="mt-0.5 text-amber-600 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-amber-900">Payment Deadline</p>
            <p className="text-xs text-amber-700">If paying later, complete payment within 7 days to secure your spot</p>
          </div>
        </div>
      </div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="text-center">
        <motion.button
          onClick={handleProceed}
          disabled={!selectedChoice}
          whileHover={selectedChoice ? { scale: 1.05 } : {}}
          whileTap={selectedChoice ? { scale: 0.95 } : {}}
          className={"inline-flex gap-3 items-center px-10 py-4 text-lg font-bold text-white rounded-xl shadow-lg transition-all " + (selectedChoice === "later" ? "bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700" : "bg-gray-300 cursor-not-allowed")}
        >
          {selectedChoice === "later" ? "Complete Registration" : "Select Pay Later to Continue"}
          <ArrowRight size={20} />
        </motion.button>
      </motion.div>
    </motion.div>
  );
}
